import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Path, ArrowClockwise, Package, Scissors, CheckCircle, PencilSimple, CaretUpDown, Check, ArrowCounterClockwise, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import ProductoCombobox from "@/components/ProductoCombobox";
import Topbar from "@/components/layout/Topbar";
import api from "@/lib/api";
import { useZebraPrinter, generarZplEtiqueta } from "@/components/ZebraPrinter";
import NumberInput from "@/components/ui/NumberInput";

const fmtKg = (n) => `${new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0)} kg`;
const fmtMoney = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 }).format(Number(n) || 0);

// Combobox interno para elegir lote origen: busca por número o descripción.
function LoteCombobox({ lotes, value, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = useMemo(() => lotes.find((l) => l.id === value), [lotes, value]);
  const filtered = useMemo(() => {
    if (!query.trim()) return lotes.slice(0, 200);
    const q = query.toLowerCase();
    return lotes
      .filter(
        (l) =>
          (l.numero || "").toLowerCase().includes(q) ||
          (l.descripcion || "").toLowerCase().includes(q) ||
          (l.codigo || "").toLowerCase().includes(q),
      )
      .slice(0, 200);
  }, [lotes, query]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          data-testid={testid}
          aria-expanded={open}
          className="flex h-9 w-full items-center justify-between rounded-sm border border-[#E5E5E5] bg-white px-2 text-[12px] hover:bg-neutral-50"
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono font-bold text-[#921A0E]">{selected.numero}</span>
              <span className="truncate">{selected.descripcion}</span>
              <span className="text-neutral-500 font-mono text-[10px]">· disp {fmtKg(selected.kilos_disponibles)}</span>
            </span>
          ) : (
            <span className="text-[#999]">Buscar por número, descripción o código…</span>
          )}
          <CaretUpDown size={12} className="ml-2 shrink-0 text-[#999]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[92vw] sm:w-[500px] p-0 rounded-sm" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar lote…"
            value={query}
            onValueChange={setQuery}
            className="text-[12px]"
          />
          <CommandList className="max-h-80">
            <CommandEmpty>
              <div className="py-4 px-3 text-center text-[12px] text-[#666]">Sin lotes que matcheen "{query}"</div>
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((l) => {
                  const isSel = value === l.id;
                  return (
                    <CommandItem
                      key={l.id}
                      value={`${l.numero}-${l.descripcion}-${l.codigo || ""}`}
                      data-testid={`lote-item-${l.id}`}
                      onSelect={() => {
                        onSelect(l.id);
                        setOpen(false);
                      }}
                      className="text-[12px] flex items-start gap-2"
                    >
                      <Check size={12} className={cn("mt-1 shrink-0", isSel ? "opacity-100" : "opacity-0")} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-[#921A0E] bg-[#F5F1E9] px-1 rounded-sm">{l.numero}</span>
                          <span className="truncate font-medium">{l.descripcion}</span>
                          {l.codigo && <span className="text-[10px] font-mono text-neutral-500">· {l.codigo}</span>}
                        </div>
                        <div className="text-[10px] text-[#666] font-mono mt-0.5">
                          Disp {fmtKg(l.kilos_disponibles)} · elab {l.fecha_elaboracion}
                          {l.costo_por_kg > 0 && ` · ${fmtMoney(l.costo_por_kg)}/kg`}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function TransformacionesPage() {
  const [lotes, setLotes] = useState([]);
  const [transformaciones, setTransformaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    // Nuevo modelo: N lotes origen (blend). El primero da el prefijo del nº de
    // lote hijo. Cada entrada es {lote_origen_id, kilos_consumidos}.
    origenes: [{ lote_origen_id: "", kilos_consumidos: "" }],
    observaciones: "",
    productos: [{ descripcion: "", codigo_corte: "", kilos: "", lote_id: null }],
  });
  const [editingTransId, setEditingTransId] = useState(null); // si != null, estamos editando

  // Edición de lote hijo
  const [editLote, setEditLote] = useState(null); // { id, transId, descripcion, kilos_entrada, fecha_elaboracion, fecha_vencimiento, consumido }
  const [savingEdit, setSavingEdit] = useState(false);

  // Revertir transformación
  const [revertTarget, setRevertTarget] = useState(null); // t
  const [reverting, setReverting] = useState(false);

  // Impresión Zebra de etiquetas de trazabilidad
  const printer = useZebraPrinter();
  const zebraConfigRef = useRef(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printTrans, setPrintTrans] = useState(null); // transformación cuyos hijos vamos a imprimir
  const [printLotes, setPrintLotes] = useState([]); // datos completos de los lotes hijos
  const [copiesByLote, setCopiesByLote] = useState({}); // { lote_id: n }
  const [printing, setPrinting] = useState(false);
  // Override del peso IMPRESO por etiqueta (no toca la DB del lote).
  // Sirve para dividir un lote en varias bandejas más chicas (ej: 5 kg → 5 bandejas de 1 kg).
  const [pesoEtiqByLote, setPesoEtiqByLote] = useState({}); // { lote_id: "1.000" }
  const [pesoEditingByLote, setPesoEditingByLote] = useState({}); // { lote_id: true }

  const pesoEtiqueta = (l) => {
    const v = pesoEtiqByLote[l.id];
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : Number(l.kilos_entrada || 0);
  };

  useEffect(() => {
    api.get("/label-templates/zebra_traceability")
      .then(({ data }) => { zebraConfigRef.current = data?.config || null; })
      .catch(() => { zebraConfigRef.current = null; });
  }, []);

  const abrirImprimir = async (t) => {
    setPrintTrans(t);
    setCopiesByLote({});
    setPrintOpen(true);
    setPrintLotes([]);
    try {
      const ids = (t.productos_resultantes || []).map((p) => p.lote_id).filter(Boolean);
      const results = await Promise.all(ids.map((id) => api.get(`/lotes/${id}`).then((r) => r.data).catch(() => null)));
      setPrintLotes(results.filter(Boolean));
    } catch (_) {
      toast.error("No se pudieron cargar los lotes");
    }
  };

  const imprimirLote = async (l, copies = 1) => {
    const baseUrl = window.location.origin;
    const qrUrl = `${baseUrl}/trazabilidad/${l.id}`;
    const zpl = generarZplEtiqueta({
      nombre: l.descripcion,
      variante: printTrans?.lote_origen_numero ? `Origen: ${printTrans.lote_origen_numero}` : "",
      peso: pesoEtiqueta(l), // ← usa el override por-etiqueta si el usuario lo cambió (varias bandejas)
      fechaElab: l.fecha_elaboracion,
      fechaVenc: l.fecha_vencimiento,
      lote: l.numero,
      qrUrl,
      frigorifico: "LA BIFERIA S.R.L.",
      cuitFrigorifico: "",
      copies,
      config: zebraConfigRef.current,
    });
    await printer.print(zpl);
  };

  const imprimirTodos = async () => {
    if (!printer.device) return toast.warning("Primero conectá la impresora Zebra");
    if (printLotes.length === 0) return;
    setPrinting(true);
    try {
      for (const l of printLotes) {
        const copies = Math.max(1, parseInt(copiesByLote[l.id] || 1));
        await imprimirLote(l, copies);
        await new Promise((r) => setTimeout(r, 250));
      }
      toast.success(`Se enviaron ${printLotes.length} etiqueta(s) a la impresora`);
    } catch (e) {
      toast.error("Error al imprimir: " + (e?.message || "desconocido"));
    } finally {
      setPrinting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lotesRes, transRes] = await Promise.all([
        api.get("/lotes", { params: { estado: "activo", limit: 500 } }),
        api.get("/transformaciones", { params: { limit: 100 } }),
      ]);
      setLotes((lotesRes.data || []).filter((l) => (l.kilos_disponibles || 0) > 0));
      setTransformaciones(transRes.data || []);
    } catch (e) {
      toast.error("No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const primerOrigen = useMemo(
    () => lotes.find((l) => l.id === form.origenes[0]?.lote_origen_id),
    [lotes, form.origenes],
  );

  const totalKgResult = useMemo(
    () => form.productos.reduce((acc, p) => acc + (parseFloat(p.kilos) || 0), 0),
    [form.productos],
  );

  const totalKgOrigenes = useMemo(
    () => form.origenes.reduce((acc, o) => acc + (parseFloat(o.kilos_consumidos) || 0), 0),
    [form.origenes],
  );

  const costoConsumido = useMemo(() => {
    // Suma ponderada: kg × costo/kg de CADA lote origen.
    return form.origenes.reduce((acc, o) => {
      const kg = parseFloat(o.kilos_consumidos) || 0;
      const l = lotes.find((x) => x.id === o.lote_origen_id);
      return acc + kg * (l?.costo_por_kg || 0);
    }, 0);
  }, [form.origenes, lotes]);

  const merma = useMemo(() => {
    // Positivo = merma (perdió peso). Negativo = ganancia (agregó líquido,
    // marinado, etc). El backend acepta ambos casos — no bloquea.
    return totalKgOrigenes - totalKgResult;
  }, [totalKgOrigenes, totalKgResult]);

  const abrirNuevo = () => {
    setEditingTransId(null);
    setForm({
      origenes: [{ lote_origen_id: "", kilos_consumidos: "" }],
      observaciones: "",
      productos: [{ descripcion: "", codigo_corte: "", kilos: "", lote_id: null, producto_id: null }],
    });
    setDlgOpen(true);
  };

  const agregarOrigen = () => {
    setForm((f) => ({ ...f, origenes: [...f.origenes, { lote_origen_id: "", kilos_consumidos: "" }] }));
  };
  const quitarOrigen = (i) => {
    setForm((f) => ({ ...f, origenes: f.origenes.filter((_, idx) => idx !== i) }));
  };
  const setOrigen = (i, patch) => {
    setForm((f) => ({ ...f, origenes: f.origenes.map((o, idx) => idx === i ? { ...o, ...patch } : o) }));
  };

  const abrirEditar = async (t) => {
    if (t.es_blend || (t.origenes || []).length > 1) {
      toast.warning("Esta transformación combina múltiples lotes. Para modificarla, revertila y creala nueva.");
      return;
    }
    setEditingTransId(t.id);
    setForm({
      origenes: [{ lote_origen_id: t.lote_origen_id, kilos_consumidos: String(t.kilos_consumidos || "") }],
      observaciones: t.observaciones || "",
      productos: (t.productos_resultantes || []).map((p) => ({
        descripcion: p.descripcion || "",
        codigo_corte: p.codigo_corte || "",
        kilos: String(p.kilos || ""),
        lote_id: p.lote_id || null,
        producto_id: p.producto_id || null,
      })),
    });
    // Aseguro que el lote origen esté disponible en la lista aunque esté agotado.
    if (!lotes.find((l) => l.id === t.lote_origen_id)) {
      try {
        const { data } = await api.get(`/lotes/${t.lote_origen_id}`);
        setLotes((prev) => [data, ...prev]);
      } catch (_) { /* ignore */ }
    }
    setDlgOpen(true);
  };

  const agregarProducto = () => {
    setForm((f) => ({ ...f, productos: [...f.productos, { descripcion: "", codigo_corte: "", kilos: "", lote_id: null, producto_id: null }] }));
  };
  const quitarProducto = (i) => {
    setForm((f) => ({ ...f, productos: f.productos.filter((_, idx) => idx !== i) }));
  };
  const setProducto = (i, patch) => {
    setForm((f) => ({ ...f, productos: f.productos.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  };

  const abrirEditarLote = async (loteRow) => {
    try {
      const { data } = await api.get(`/lotes/${loteRow.lote_id}`);
      const consumido = Math.max(
        0,
        Number(data.kilos_entrada || 0) - Number(data.kilos_disponibles || 0),
      );
      setEditLote({
        id: data.id,
        descripcion: data.descripcion || "",
        kilos_entrada: String(data.kilos_entrada ?? loteRow.kilos ?? ""),
        fecha_elaboracion: data.fecha_elaboracion || "",
        fecha_vencimiento: data.fecha_vencimiento || "",
        consumido: Math.round(consumido * 1000) / 1000,
        costo_total: data.costo_total || 0,
        numero: data.numero,
      });
    } catch (e) {
      toast.error("No se pudo cargar el lote para editar");
    }
  };

  const guardarEdicionLote = async () => {
    if (!editLote) return;
    const kilos = parseFloat(editLote.kilos_entrada);
    if (!editLote.descripcion.trim()) return toast.warning("Descripción requerida");
    if (!Number.isFinite(kilos) || kilos <= 0) return toast.warning("Kilos inválidos");
    if (kilos < (editLote.consumido || 0) - 0.001) {
      return toast.warning(`No se puede reducir a ${kilos} kg: ya se consumieron ${editLote.consumido} kg`);
    }
    setSavingEdit(true);
    try {
      await api.put(`/lotes/${editLote.id}`, {
        descripcion: editLote.descripcion.trim(),
        kilos_entrada: kilos,
        fecha_elaboracion: editLote.fecha_elaboracion || null,
        fecha_vencimiento: editLote.fecha_vencimiento || null,
      });
      toast.success("Lote actualizado");
      setEditLote(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSavingEdit(false);
    }
  };

  const revertir = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      const { data } = await api.delete(`/transformaciones/${revertTarget.id}`);
      toast.success(`Transformación revertida · ${data.reverted_children} lote(s) hijo(s) eliminados`);
      setRevertTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    } finally {
      setReverting(false);
    }
  };

  const guardar = async () => {
    const origenesValidos = form.origenes
      .filter((o) => o.lote_origen_id && parseFloat(o.kilos_consumidos) > 0)
      .map((o) => ({
        lote_origen_id: o.lote_origen_id,
        kilos_consumidos: parseFloat(o.kilos_consumidos),
      }));
    if (origenesValidos.length === 0) return toast.warning("Elegí al menos un lote origen con sus kilos");
    // Validar disponibilidad en cada origen (excepto en edición del origen mismo).
    if (!editingTransId) {
      for (const o of origenesValidos) {
        const l = lotes.find((x) => x.id === o.lote_origen_id);
        if (l && o.kilos_consumidos > l.kilos_disponibles + 0.001) {
          return toast.warning(`El lote ${l.numero} sólo tiene ${l.kilos_disponibles} kg disponibles`);
        }
      }
    }
    const productos = form.productos
      .filter((p) => (parseFloat(p.kilos) || 0) > 0 && (p.descripcion || "").trim())
      .map((p) => ({
        lote_id: p.lote_id || null,
        descripcion: p.descripcion.trim(),
        codigo_corte: (p.codigo_corte || "").trim().toUpperCase(),
        kilos: parseFloat(p.kilos),
        producto_id: p.producto_id || null,
      }));
    if (productos.length === 0) return toast.warning("Ingresá al menos un producto resultante con descripción y kilos");
    if (productos.some((p) => !p.codigo_corte)) return toast.warning("Cada producto resultante necesita un código de corte para el sufijo del lote");
    // Regla dura: cada resultante DEBE apuntar al catálogo (Productos y
    // Precios). Sin esto, se crean lotes huérfanos tipo "HAMBURGUESA BLEND".
    const sinCatalogo = productos.find((p) => !p.producto_id);
    if (sinCatalogo) {
      return toast.warning(
        `El resultante "${sinCatalogo.descripcion}" no está vinculado al catálogo. Elegilo del combobox de Productos y Precios.`,
      );
    }
    setSaving(true);
    try {
      if (editingTransId) {
        // En edición sólo se soporta 1 origen (el backend rechaza blends editados).
        await api.put(`/transformaciones/${editingTransId}`, {
          kilos_consumidos: origenesValidos[0].kilos_consumidos,
          productos_resultantes: productos,
          observaciones: form.observaciones || null,
        });
        toast.success("Transformación actualizada · lotes hijos recalculados");
      } else {
        const { data } = await api.post("/transformaciones", {
          origenes: origenesValidos,
          productos_resultantes: productos.map(({ lote_id, ...rest }) => rest),
          observaciones: form.observaciones || null,
        });
        const blendLbl = origenesValidos.length > 1 ? ` (blend de ${origenesValidos.length} lotes)` : "";
        toast.success(`Transformación creada${blendLbl} · ${data.lotes_creados.length} lotes hijos`);
      }
      setDlgOpen(false);
      setEditingTransId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingTransId ? "No se pudo editar" : "No se pudo crear la transformación"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Topbar title="Transformaciones" subtitle="Despiece de materia prima · consume un lote origen y genera lotes hijos con costo proporcional" />
      <div className="p-4 space-y-4" data-testid="transformaciones-page">
        <div className="flex items-center gap-2">
          <Button onClick={abrirNuevo} className="gap-2 bg-[#921A0E] hover:bg-[#7a1409] text-white" data-testid="btn-nueva-transformacion">
            <Scissors size={16} /> Nueva transformación
          </Button>
          <Button onClick={load} variant="outline" className="gap-2" data-testid="btn-reload-trans">
            <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} /> Actualizar
          </Button>
          <div className="ml-auto text-sm text-neutral-500 font-mono">
            {lotes.length} lote{lotes.length === 1 ? "" : "s"} disponibles para despiezar
          </div>
        </div>

        {transformaciones.length === 0 ? (
          <div className="text-center py-16 border rounded-lg bg-neutral-50 text-neutral-400">
            No hay transformaciones registradas todavía.
          </div>
        ) : (
          <div className="space-y-3">
            {transformaciones.map((t) => (
              <div key={t.id} className="border border-neutral-200 rounded-lg bg-white p-4" data-testid={`trans-row-${t.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Scissors size={18} className="text-purple-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(t.origenes && t.origenes.length > 1) ? (
                        <>
                          <Badge variant="secondary" className="text-[10px] bg-purple-100 text-purple-800 border-purple-200">
                            BLEND · {t.origenes.length} lotes
                          </Badge>
                          {t.origenes.map((o, idx) => (
                            <span key={o.lote_origen_id || idx} className="inline-flex items-center gap-1" data-testid={`trans-origen-${t.id}-${idx}`}>
                              {idx > 0 && <span className="text-neutral-300">+</span>}
                              <span className="font-mono font-bold text-[#921A0E]">Lote {o.lote_origen_numero}</span>
                              <span className="text-neutral-400">→</span>
                              <span className="text-sm">{o.lote_origen_descripcion}</span>
                              <span className="text-[11px] text-neutral-500 font-mono">({fmtKg(o.kilos_consumidos)})</span>
                            </span>
                          ))}
                        </>
                      ) : (
                        <>
                          <span className="font-mono font-bold text-[#921A0E]">Lote {t.lote_origen_numero}</span>
                          <span className="text-neutral-400">→</span>
                          <span className="text-sm">{t.lote_origen_descripcion}</span>
                        </>
                      )}
                      <Badge variant="outline" className="text-[10px] ml-2">{t.fecha}</Badge>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      Consumido <strong>{fmtKg(t.kilos_consumidos)}</strong> · resultante <strong>{fmtKg(t.kilos_totales_resultantes)}</strong>
                      {t.merma_kg > 0 && <> · merma <span className="text-amber-700">{fmtKg(t.merma_kg)}</span></>}
                      {t.merma_kg < 0 && <> · ganancia <span className="text-emerald-700">+{fmtKg(Math.abs(t.merma_kg))}</span></>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => abrirImprimir(t)}
                    className="gap-1 h-8 text-xs border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                    data-testid={`btn-print-trans-${t.id}`}
                  >
                    <Printer size={12} /> Etiquetas
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => abrirEditar(t)}
                    className="gap-1 h-8 text-xs"
                    data-testid={`btn-edit-trans-${t.id}`}
                  >
                    <PencilSimple size={12} /> Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRevertTarget(t)}
                    className="gap-1 h-8 text-xs border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    data-testid={`btn-revert-trans-${t.id}`}
                  >
                    <ArrowCounterClockwise size={12} /> Revertir
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(t.productos_resultantes || []).map((r) => (
                    <div key={r.lote_id} className="border border-neutral-200 rounded px-2 py-1.5 flex items-center gap-2 text-xs bg-neutral-50 group">
                      <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{r.descripcion}</div>
                        <div className="text-neutral-500 font-mono text-[10px]">Lote {r.lote_numero}</div>
                      </div>
                      <div className="text-right font-mono">
                        <div className="font-bold">{fmtKg(r.kilos)}</div>
                        {r.costo_por_kg > 0 && <div className="text-[10px] text-neutral-500">{fmtMoney(r.costo_por_kg)}/kg</div>}
                      </div>
                      <NumberInput
                        min="1"
                        step="1"
                        value={copiesByLote[r.lote_id] ?? 1}
                        onChange={(e) => setCopiesByLote((s) => ({ ...s, [r.lote_id]: Math.max(1, parseInt(e.target.value) || 1) }))}
                        className="w-12 h-7 text-center text-xs font-mono px-1"
                        title="Cantidad de copias a imprimir"
                        data-testid={`copies-inline-${r.lote_id}`}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          if (!printer.device) {
                            toast.warning("Primero conectá la impresora Zebra");
                            return;
                          }
                          const copies = Math.max(1, parseInt(copiesByLote[r.lote_id] || 1));
                          try {
                            const { data: lote } = await api.get(`/lotes/${r.lote_id}`);
                            setPrintTrans(t);
                            const baseUrl = window.location.origin;
                            const qrUrl = `${baseUrl}/trazabilidad/${lote.id}`;
                            const zpl = generarZplEtiqueta({
                              nombre: lote.descripcion,
                              variante: `Origen: ${t.lote_origen_numero}`,
                              peso: lote.kilos_entrada,
                              fechaElab: lote.fecha_elaboracion,
                              fechaVenc: lote.fecha_vencimiento,
                              lote: lote.numero,
                              qrUrl,
                              frigorifico: "LA BIFERIA S.R.L.",
                              cuitFrigorifico: "",
                              copies,
                              config: zebraConfigRef.current,
                            });
                            await printer.print(zpl);
                            toast.success(`${copies} etiqueta(s) ${lote.numero} enviada(s)`);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "No se pudo imprimir");
                          }
                        }}
                        className="ml-1 p-1 text-neutral-400 hover:text-[#921A0E] hover:bg-white rounded transition"
                        title="Imprimir etiqueta Zebra"
                        data-testid={`btn-print-lote-${r.lote_id}`}
                      >
                        <Printer size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => abrirEditarLote(r)}
                        className="ml-1 p-1 text-neutral-400 hover:text-[#921A0E] hover:bg-white rounded transition"
                        title="Editar lote"
                        data-testid={`btn-edit-lote-${r.lote_id}`}
                      >
                        <PencilSimple size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                {t.observaciones && (
                  <div className="mt-2 border border-amber-200 bg-amber-50/40 rounded p-2 text-xs">
                    <div className="text-[10px] uppercase text-amber-700 font-mono font-bold">Observaciones</div>
                    <div className="text-neutral-800 whitespace-pre-wrap">{t.observaciones}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal Nueva transformación */}
        <Dialog open={dlgOpen} onOpenChange={(v) => { setDlgOpen(v); if (!v) setEditingTransId(null); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editingTransId ? "Editar transformación" : "Nueva transformación"}</DialogTitle>
              {editingTransId && (
                <DialogDescription>
                  Podés modificar kilos consumidos y productos resultantes. Los lotes hijos existentes se recalculan; los quitados requieren no tener consumos.
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase font-mono text-neutral-500">
                    Lotes origen {form.origenes.length > 1 && <span className="normal-case text-[10px] text-[#921A0E] font-semibold ml-1">(BLEND)</span>}
                  </label>
                  {!editingTransId && (
                    <Button size="sm" variant="outline" onClick={agregarOrigen} className="gap-1 text-xs h-7" data-testid="btn-add-origen">
                      <Plus size={12} /> Agregar origen
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {form.origenes.map((o, i) => {
                    const l = lotes.find((x) => x.id === o.lote_origen_id);
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`trans-origen-row-${i}`}>
                        <div className="col-span-7">
                          {editingTransId ? (
                            <div className="flex h-9 w-full items-center rounded-sm border border-dashed border-neutral-300 bg-neutral-50 px-2 text-[12px] text-neutral-600">
                              {primerOrigen ? (
                                <span className="flex items-center gap-2 truncate">
                                  <span className="font-mono font-bold text-[#921A0E]">{primerOrigen.numero}</span>
                                  <span className="truncate">{primerOrigen.descripcion}</span>
                                </span>
                              ) : <span className="text-neutral-400">Cargando…</span>}
                              <span className="ml-auto text-[10px] uppercase font-mono text-neutral-400">no editable</span>
                            </div>
                          ) : (
                            <LoteCombobox
                              lotes={lotes}
                              value={o.lote_origen_id}
                              onSelect={(id) => setOrigen(i, { lote_origen_id: id })}
                              testid={`trans-lote-origen-${i}`}
                            />
                          )}
                        </div>
                        <NumberInput step="0.001" min="0"
                          placeholder="kg a consumir"
                          value={o.kilos_consumidos}
                          onChange={(e) => setOrigen(i, { kilos_consumidos: e.target.value })}
                          className="col-span-4 text-right font-mono"
                          data-testid={`trans-origen-kg-${i}`}
                        />
                        {!editingTransId && (
                          <button
                            type="button"
                            onClick={() => quitarOrigen(i)}
                            disabled={form.origenes.length <= 1}
                            className="col-span-1 text-neutral-400 hover:text-red-600 disabled:opacity-30"
                            title="Quitar este origen"
                          >
                            <Trash size={16} />
                          </button>
                        )}
                        {l && (
                          <div className="col-span-12 text-[11px] text-neutral-500 font-mono pl-1">
                            {l.fecha_elaboracion} · disp {fmtKg(l.kilos_disponibles)} · costo/kg {fmtMoney(l.costo_por_kg || 0)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-xs font-mono text-neutral-600 flex items-center justify-between">
                  <div>
                    Total a consumir: <strong>{fmtKg(totalKgOrigenes)}</strong>
                    {costoConsumido > 0 && <> · Costo <strong className="text-[#921A0E]">{fmtMoney(costoConsumido)}</strong></>}
                  </div>
                  {form.origenes.length > 1 && (
                    <div className="text-[10px] text-[#921A0E]">
                      Los lotes hijos van a heredar el prefijo <strong>{primerOrigen?.numero || "…"}+</strong>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase font-mono text-neutral-500">Productos resultantes</label>
                  <Button size="sm" variant="outline" onClick={agregarProducto} className="gap-1 text-xs h-7" data-testid="btn-add-prod">
                    <Plus size={12} /> Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.productos.map((p, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`trans-prod-row-${i}`}>
                      <div className="col-span-6">
                        <ProductoCombobox
                          kind="productos"
                          value={{ codigo: p.codigo_corte || "", nombre: p.descripcion || "" }}
                          onSelect={(prod) =>
                            setProducto(i, {
                              descripcion: prod.nombre || "",
                              // Auto-completa el código de corte con el código del producto seleccionado.
                              codigo_corte: (prod.codigo || "").toUpperCase(),
                              producto_id: prod.id || null,
                            })
                          }
                          placeholder="Buscar corte por código o nombre…"
                          testid={`trans-prod-combo-${i}`}
                        />
                        {!p.producto_id && p.descripcion && (
                          <div className="text-[10px] text-red-700 mt-1 font-mono font-bold" data-testid={`trans-prod-warn-${i}`}>
                            ⛔ No vinculado al catálogo — elegilo de Productos y Precios.
                          </div>
                        )}
                      </div>
                      <Input
                        placeholder="Corte (sufijo lote)"
                        value={p.codigo_corte}
                        onChange={(e) => setProducto(i, { codigo_corte: e.target.value.toUpperCase() })}
                        className="col-span-3 font-mono uppercase"
                        title="Se usará como sufijo del número de lote hijo. Ej: 032-26-BONDIOLA"
                        data-testid={`trans-prod-codigo-${i}`}
                      />
                      <NumberInput step="0.001" min="0"
                        placeholder="kg"
                        value={p.kilos}
                        onChange={(e) => setProducto(i, { kilos: e.target.value })}
                        className="col-span-2 text-right font-mono"
                        data-testid={`trans-prod-kilos-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => quitarProducto(i)}
                        disabled={form.productos.length <= 1}
                        className="col-span-1 text-neutral-400 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs font-mono text-neutral-600">
                  <div>
                    Total resultante: <strong>{fmtKg(totalKgResult)}</strong>
                    {merma > 0.001 && <> · Merma: <strong className="text-amber-700">{fmtKg(merma)}</strong></>}
                    {merma < -0.001 && <> · Ganancia (agregado): <strong className="text-emerald-700">+{fmtKg(Math.abs(merma))}</strong></>}
                  </div>
                  {primerOrigen && totalKgOrigenes > 0 && (
                    <div className="text-neutral-500">
                      Los lotes hijos heredarán el prefijo <strong className="text-[#921A0E]">{primerOrigen.numero}{form.origenes.length > 1 ? "+" : ""}-…</strong>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase font-mono text-neutral-500 mb-1 block">Observaciones</label>
                <Input
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  placeholder="Notas del despiece..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
              <Button
                onClick={guardar}
                disabled={saving}
                className="bg-[#921A0E] hover:bg-[#7a1409] text-white gap-2"
                data-testid="btn-confirm-trans"
              >
                {saving ? <ArrowClockwise size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                {editingTransId ? "Guardar cambios" : "Confirmar transformación"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Editar lote hijo */}
        <Dialog open={!!editLote} onOpenChange={(v) => !v && setEditLote(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Editar lote {editLote?.numero}</DialogTitle>
              <DialogDescription>
                Cambios permitidos: descripción, kilos totales y fechas. El costo por kg se
                recalcula automáticamente si modificás los kilos.
              </DialogDescription>
            </DialogHeader>
            {editLote && (
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px]">Descripción</Label>
                  <Input
                    value={editLote.descripcion}
                    onChange={(e) => setEditLote({ ...editLote, descripcion: e.target.value })}
                    data-testid="edit-lote-desc"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Kilos totales (entrada)</Label>
                  <NumberInput
                    step="0.001"
                    min="0"
                    value={editLote.kilos_entrada}
                    onChange={(e) => setEditLote({ ...editLote, kilos_entrada: e.target.value })}
                    data-testid="edit-lote-kilos"
                  />
                  {editLote.consumido > 0 && (
                    <div className="text-[11px] text-amber-700 mt-1 font-mono">
                      Ya se consumieron {fmtKg(editLote.consumido)} de este lote. No podés reducir por debajo de ese valor.
                    </div>
                  )}
                  {editLote.costo_total > 0 && parseFloat(editLote.kilos_entrada) > 0 && (
                    <div className="text-[11px] text-neutral-500 mt-1 font-mono">
                      Nuevo costo/kg: <strong>{fmtMoney(editLote.costo_total / parseFloat(editLote.kilos_entrada))}</strong>
                      {" "}(costo total {fmtMoney(editLote.costo_total)} / {editLote.kilos_entrada} kg)
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[12px]">Fecha de elaboración</Label>
                    <Input
                      type="date"
                      value={editLote.fecha_elaboracion || ""}
                      onChange={(e) => setEditLote({ ...editLote, fecha_elaboracion: e.target.value })}
                      data-testid="edit-lote-fecha-elab"
                    />
                  </div>
                  <div>
                    <Label className="text-[12px]">Fecha de vencimiento</Label>
                    <Input
                      type="date"
                      value={editLote.fecha_vencimiento || ""}
                      onChange={(e) => setEditLote({ ...editLote, fecha_vencimiento: e.target.value })}
                      data-testid="edit-lote-fecha-venc"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditLote(null)}>Cancelar</Button>
              <Button
                onClick={guardarEdicionLote}
                disabled={savingEdit}
                className="bg-[#921A0E] hover:bg-[#7a1409] text-white gap-2"
                data-testid="btn-save-edit-lote"
              >
                {savingEdit ? <ArrowClockwise size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Guardar cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmación de revertir transformación */}
        <AlertDialog open={!!revertTarget} onOpenChange={(v) => !v && !reverting && setRevertTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revertir transformación</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminarán los <strong>{(revertTarget?.productos_resultantes || []).length} lote(s) hijo(s)</strong> y se devolverán
                {" "}<strong>{fmtKg(revertTarget?.kilos_consumidos || 0)}</strong> al lote origen{" "}
                <span className="font-mono text-[#921A0E]">{revertTarget?.lote_origen_numero}</span>.
                <br />
                <span className="text-red-700 text-[12px]">Sólo se permite si ningún lote hijo tiene consumos registrados.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={reverting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={revertir}
                disabled={reverting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="btn-confirm-revert"
              >
                {reverting ? "Revirtiendo…" : "Revertir"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog Imprimir etiquetas Zebra de trazabilidad */}
        <Dialog open={printOpen} onOpenChange={(v) => !printing && setPrintOpen(v)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer size={20} className="text-[#921A0E]" />
                Etiquetas de trazabilidad
              </DialogTitle>
              <DialogDescription>
                Lotes hijos generados por la transformación del lote{" "}
                <span className="font-mono font-bold text-[#921A0E]">{printTrans?.lote_origen_numero}</span>.
              </DialogDescription>
            </DialogHeader>

            <div className="mb-3 border border-neutral-200 rounded-lg bg-neutral-50 p-3 flex items-center justify-between">
              <div className="text-sm">
                <span className="font-mono uppercase text-neutral-500 text-xs">Impresora Zebra: </span>
                {printer.device ? (
                  <span className="font-medium text-emerald-700">✓ {printer.device.productName || "Conectada"}</span>
                ) : (
                  <span className="text-neutral-500">no conectada</span>
                )}
              </div>
              {!printer.device && (
                <Button size="sm" onClick={printer.connect} disabled={printer.connecting || !printer.supported}
                  className="gap-1" data-testid="trans-zebra-conectar">
                  <Printer size={14} /> {printer.connecting ? "Conectando…" : "Conectar Zebra"}
                </Button>
              )}
            </div>

            {!printer.supported && (
              <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
                Este navegador no soporta WebUSB. Abrí el ERP en Chrome, Edge u Opera para poder imprimir directamente.
              </div>
            )}

            <div className="border border-neutral-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr className="text-left uppercase font-mono text-[10px] text-neutral-500">
                    <th className="px-3 py-2">Lote</th>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2 text-right">Kg</th>
                    <th className="px-3 py-2 w-24 text-center">Copias</th>
                    <th className="px-3 py-2 w-20 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {printLotes.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-400">Cargando lotes…</td></tr>
                  ) : printLotes.map((l) => (
                    <tr key={l.id} className="border-t border-neutral-100" data-testid={`print-lote-row-${l.id}`}>
                      <td className="px-3 py-2 font-mono font-bold text-[#921A0E]">{l.numero}</td>
                      <td className="px-3 py-2">{l.descripcion}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {(() => {
                          const editing = !!pesoEditingByLote[l.id];
                          const override = pesoEtiqByLote[l.id];
                          const overrideNum = parseFloat(override);
                          const hasOverride = Number.isFinite(overrideNum) && overrideNum > 0
                            && Math.abs(overrideNum - Number(l.kilos_entrada || 0)) > 0.001;
                          if (!editing) {
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  setPesoEditingByLote((s) => ({ ...s, [l.id]: true }));
                                  if (pesoEtiqByLote[l.id] === undefined) {
                                    setPesoEtiqByLote((s) => ({ ...s, [l.id]: String(l.kilos_entrada ?? "") }));
                                  }
                                }}
                                title="Peso por etiqueta (para dividir el lote en varias bandejas). No modifica el lote."
                                className={`inline-flex items-center gap-1 hover:underline decoration-dotted ${hasOverride ? "text-emerald-700 font-bold" : "text-[#921A0E]"}`}
                                data-testid={`print-lote-peso-edit-${l.id}`}
                              >
                                {hasOverride ? `${fmtKg(overrideNum)} / etiq.` : fmtKg(l.kilos_entrada)}
                                <PencilSimple size={11} className="opacity-60" />
                              </button>
                            );
                          }
                          return (
                            <div className="inline-flex items-center gap-1 justify-end">
                              <NumberInput min="0.001" step="0.001"
                                value={pesoEtiqByLote[l.id] ?? ""}
                                onChange={(e) => setPesoEtiqByLote((s) => ({ ...s, [l.id]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); setPesoEditingByLote((s) => ({ ...s, [l.id]: false })); }
                                  if (e.key === "Escape") {
                                    setPesoEditingByLote((s) => ({ ...s, [l.id]: false }));
                                    setPesoEtiqByLote((s) => { const c = { ...s }; delete c[l.id]; return c; });
                                  }
                                }}
                                autoFocus
                                className="w-20 h-7 text-right font-mono"
                                data-testid={`print-lote-peso-input-${l.id}`}
                              />
                              <Button size="sm" variant="outline"
                                      onClick={() => setPesoEditingByLote((s) => ({ ...s, [l.id]: false }))}
                                      className="h-7 px-2 text-[11px]"
                                      data-testid={`print-lote-peso-save-${l.id}`}>
                                OK
                              </Button>
                              <Button size="sm" variant="ghost"
                                      onClick={() => {
                                        setPesoEditingByLote((s) => ({ ...s, [l.id]: false }));
                                        setPesoEtiqByLote((s) => { const c = { ...s }; delete c[l.id]; return c; });
                                      }}
                                      className="h-7 px-1 text-[11px] text-neutral-500">
                                ✕
                              </Button>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <NumberInput min="1" step="1"
                          value={copiesByLote[l.id] ?? 1}
                          onChange={(e) => setCopiesByLote((s) => ({ ...s, [l.id]: Math.max(1, parseInt(e.target.value) || 1) }))}
                          className="w-16 h-7 text-center font-mono mx-auto"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!printer.device || printing}
                          onClick={async () => {
                            try {
                              await imprimirLote(l, Math.max(1, parseInt(copiesByLote[l.id] || 1)));
                              toast.success(`Etiqueta ${l.numero} enviada`);
                            } catch (e) {
                              toast.error("No se pudo imprimir");
                            }
                          }}
                          className="h-7 text-[11px] gap-1"
                          data-testid={`btn-print-one-${l.id}`}
                        >
                          <Printer size={11} /> Imprimir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPrintOpen(false)} disabled={printing}>Cerrar</Button>
              <Button
                onClick={imprimirTodos}
                disabled={printing || !printer.device || printLotes.length === 0}
                className="bg-[#921A0E] hover:bg-[#7a1409] text-white gap-2"
                data-testid="btn-print-all-trans"
              >
                {printing ? <ArrowClockwise size={14} className="animate-spin" /> : <Printer size={14} />}
                Imprimir todas
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
